import React, { useState, useEffect } from 'react';
import {
    Box,
    Select,
    MenuItem,
    SelectChangeEvent,
    Typography,
    Switch,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGlobalData } from '../context/GlobalDataContext';
import GlobalDataQuery from '../../../services/query/globaldata/GlobalDataQuery.services';
import Swal from 'sweetalert2';
import { GlobaldataCommand } from '../../../services/command';
import { GD } from '../globalData.classes';

interface SupportedCurrency {
    currencyId: number;
    symbol: string;
    shortCode: string;
    shortCodeAnalytics?: string;
    precision: number;
    description: string;
    displayOrder: number;
    isDefault: boolean;
    id?: number;
    lastUpdateDate?: string;
    lastUpdatedBy?: string;
}

export const SupportedCurrencies = () => {
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const [countries, setCountries] = useState<any[]>([]);
    const [supportedCurrencies, setSupportedCurrencies] = useState<SupportedCurrency[]>([]);

    const { t } = useTranslation();
    const { setIsLoading, isLoading } = useGlobalData();

    const handleSelectChange = (event: SelectChangeEvent<string>) => {
        setSelectedCountry(event.target.value);
    };

    const handleDefaultCurrencyChange = async (currency: SupportedCurrency, newIsDefault: boolean) => {
        if (!newIsDefault || currency.isDefault) {
            return;
        }

        setIsLoading(true);
        try {
            // Tüm aktif currency'leri pasife çek
            const activeCurrencies = supportedCurrencies.filter(
                (c: SupportedCurrency) => c.id !== currency.id && c.isDefault
            );

            const updatePromises = activeCurrencies.map((c: SupportedCurrency) =>
                GlobaldataCommand.UpdateSupportedCurrency({
                    countryId: selectedCountry,
                    currencyId: c.currencyId,
                    isDefault: false,
                })
            );

            // Seçilen currency'yi aktife çek
            updatePromises.push(
                GlobaldataCommand.UpdateSupportedCurrency({
                    countryId: selectedCountry,
                    currencyId: currency.currencyId,
                    isDefault: true,
                })
            );

            await Promise.all(updatePromises);

            getSupportedCurrencies(true);

            Swal.fire({
                position: 'center',
                icon: 'success',
                title: t('general.success'),
                showConfirmButton: false,
                timer: 1500,
            });
        } catch (error: any) {
            console.error('Error updating default currency:', error);
            Swal.fire(t('general.anErrorOccured'), '', 'error');
            setIsLoading(false);
        }
    };

    const getSupportedCurrencies = (resetCache: boolean = false) => {
        if (!selectedCountry) return;
        GlobalDataQuery.GetSupportedCurrencies({
            CountryId: selectedCountry,
            resetCache: resetCache,
        })
            .then((res: any) => {
                if (res) {
                    setIsLoading(false);
                    const currencies = res.data?.supportedCurrencies || [];
                    const sortedCurrencies = [...currencies].sort(
                        (a: SupportedCurrency, b: SupportedCurrency) => a.displayOrder - b.displayOrder
                    );
                    setSupportedCurrencies(sortedCurrencies);
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
                setIsLoading(false);
            });
    };

    const columns = [
        {
            field: 'displayOrder',
            headerName: t('globalData.rowNumber'),
            width: 80,
            align: 'center',
        },
        {
            field: 'symbol',
            headerName: t('globalData.currencySymbol'),
            width: 100,
            align: 'center',
        },
        {
            field: 'shortCode',
            headerName: t('globalData.currencyShortCode'),
            width: 120,
            align: 'center',
        },
        {
            field: 'description',
            headerName: t('globalData.currencyDescription'),
            width: 200,
            align: 'center',
        },
        {
            field: 'isDefault',
            headerName: t('globalData.defaultCurrency'),
            width: 120,
            align: 'center',
            renderCell: (params: any) => {
                const isDisabled = params.row.isDefault || isLoading;
                return (
                    <Switch
                        className={GD.inputSwitchDefaultCurrency}
                        checked={params.row.isDefault}
                        color="primary"
                        onChange={(e) => handleDefaultCurrencyChange(params.row, e.target.checked)}
                        disabled={isDisabled}
                    />
                );
            },
        },
    ];

    useEffect(() => {
        setIsLoading(true);
        GlobalDataQuery.GetCountries()
            .then((res: any) => {
                if (res) {
                    setCountries(res.data);
                    if (res.data?.length > 0) {
                        setSelectedCountry(res.data[0].countryId);
                    } else {
                        setIsLoading(false);
                    }
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        setIsLoading(true);
        if (selectedCountry) {
            getSupportedCurrencies();
        }
    }, [selectedCountry]);

    return (
        <Box className={GD.currenciesRoot} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box
                className={GD.currenciesToolbar}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: '12px',
                    width: '100%',
                }}
            >
                <Typography variant="body1" sx={{ mr: 1 }}>
                    {t('globalData.selectCountry')}:
                </Typography>
                <Box className={GD.currenciesCountryFieldWrap}>
                <Select
                    className={GD.inputSelectCountry}
                    value={selectedCountry}
                    onChange={handleSelectChange}
                    sx={{ minWidth: 120 }}
                >
                    {(countries || []).map((item) => (
                        <MenuItem key={item.countryId} value={item.countryId}>
                            {item.countryName}
                        </MenuItem>
                    ))}
                </Select>
                </Box>
            </Box>
            <Box className={GD.currenciesTableWrap} sx={{ marginTop: '12px', width: '100%' }}>
                <Table className={GD.currenciesTable}>
                    <TableHead>
                        <TableRow>
                            {columns.map((col: any) => (
                                <TableCell key={col.field} align={col.align || 'left'} style={{ width: col.width }}>
                                    <h3>{col.headerName}</h3>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {supportedCurrencies.map((row: any) => (
                            <TableRow key={row.id || row.currencyId}>
                                {columns.map((col: any) => (
                                    <TableCell
                                        key={`${row.id || row.currencyId}-${col.field}`}
                                        align={col.align || 'left'}
                                        style={{ width: col.width }}
                                    >
                                        {col.renderCell ? col.renderCell({ row }) : row[col.field]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </Box>
    );
};
